import { type ReactNode, useEffect, useRef } from 'react';
import { motion, AnimatePresence, type PanInfo } from 'framer-motion';
import { useGameStore } from '@/state/gameStore';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Title for the drag handle area, optional. */
  title?: string;
  /** Maximum height as a CSS value (default '90vh'). */
  maxHeight?: string;
  /** ARIA label. */
  ariaLabel?: string;
}

const SWIPE_DOWN_VELOCITY = 500;
const SWIPE_DOWN_THRESHOLD_PX = 120;

/**
 * Mobile-friendly bottom sheet. DIY framer-motion — no external dependency.
 * - Slides up from the bottom edge.
 * - Drag the handle to dismiss (downward swipe).
 * - Tapping the backdrop closes.
 * - ESC closes.
 * - Honors uiPrefs.reduceMotion.
 */
export function BottomSheet({
  open,
  onClose,
  children,
  title,
  maxHeight = '90vh',
  ariaLabel,
}: BottomSheetProps) {
  const reduceMotion = useGameStore((s) => s.uiPrefs.reduceMotion);
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const dur = reduceMotion ? 0.08 : 0.25;

  const handleDragEnd = (
    _: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ) => {
    if (info.velocity.y > SWIPE_DOWN_VELOCITY || info.offset.y > SWIPE_DOWN_THRESHOLD_PX) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="bs-root"
          className="fixed inset-0 z-50 flex flex-col justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: dur }}
        >
          <div
            className="absolute inset-0 bg-bg-overlay backdrop-blur-sm"
            onClick={onClose}
            aria-hidden
          />
          <motion.aside
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel ?? title ?? 'Sheet'}
            className="relative w-full overflow-hidden rounded-t-2xl border-t border-border-strong bg-bg-secondary shadow-2xl"
            style={{ maxHeight }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: dur, ease: 'easeOut' }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={handleDragEnd}
          >
            {/* Drag handle */}
            <div className="flex flex-col items-center pt-2 pb-1 cursor-grab touch-none">
              <span className="h-1 w-10 rounded-full bg-fg-muted/50" aria-hidden />
              {title && (
                <p className="mt-2 px-4 text-sm font-semibold text-fg-primary">{title}</p>
              )}
            </div>
            <div
              className="overflow-y-auto"
              style={{ maxHeight: `calc(${maxHeight} - 48px)` }}
            >
              {children}
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
